import { useState } from "react";
import { Heart, MessageCircle, Pencil, Trash2, Flag } from "lucide-react";
import { toast } from "sonner";
import fanApi from "@/lib/fanApi";
import { useFanAuth } from "@/context/FanAuthContext";
import CommentComposer from "./CommentComposer";
import { Avatar, UserBadges, timeAgo } from "./CommentMeta";

const ActionBtn = ({ icon: Icon, label, onClick, active, testId, activeColor }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
    style={active ? { color: activeColor } : undefined}
    data-testid={testId}
  >
    <Icon className="w-3.5 h-3.5" fill={active && Icon === Heart ? "currentColor" : "none"} /> {label}
  </button>
);

export default function CommentItem({ comment, accent, onReply, onChange, onDelete, isReply }) {
  const { user, login } = useFanAuth();
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);
  const canDelete = comment.is_mine || user?.role === "moderator";
  const canAct = !user?.banned;
  const safe = (fn) => async () => {
    try {
      await fn();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Algo salió mal, intenta de nuevo");
    }
  };
  const requireAuth = (fn) => (user ? safe(fn) : login);

  const like = requireAuth(async () => {
    const res = await fanApi.post(`/comments/${comment.id}/like`);
    onChange({ ...comment, liked_by_me: res.data.liked, like_count: res.data.like_count });
  });

  const report = requireAuth(async () => {
    if (comment.reported_by_me || !window.confirm("¿Reportar este comentario como ofensivo?")) return;
    await fanApi.post(`/comments/${comment.id}/report`);
    onChange({ ...comment, reported_by_me: true });
    toast.success("Gracias, lo revisaremos");
  });

  const remove = safe(async () => {
    if (!window.confirm("¿Borrar este comentario?")) return;
    await fanApi.delete(`/comments/${comment.id}`);
    onDelete(comment.id);
  });

  const saveEdit = async (content) => {
    try {
      const res = await fanApi.put(`/comments/${comment.id}`, { content });
      onChange({ ...comment, ...res.data });
      setEditing(false);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo editar");
      return false;
    }
  };

  const sendReply = async (content) => {
    const ok = await onReply(content);
    if (ok) setReplying(false);
    return ok;
  };

  return (
    <div className="flex gap-3" data-testid={`comment-${comment.id}`}>
      <Avatar author={comment.author} size={isReply ? "w-7 h-7" : "w-9 h-9"} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-white text-sm">{comment.author.name}</span>
          <UserBadges author={comment.author} />
          <span className="text-[11px] text-gray-500">
            {timeAgo(comment.created_at)}{comment.edited_at && " · editado"}
          </span>
        </div>
        {editing ? (
          <div className="mt-2">
            <CommentComposer initial={comment.content} onSubmit={saveEdit} onCancel={() => setEditing(false)} submitLabel="Guardar" accent={accent} autoFocus testId="comment-edit" />
          </div>
        ) : (
          <p className="text-gray-200 text-sm mt-1 whitespace-pre-wrap break-words" data-testid="comment-content">{comment.content}</p>
        )}
        <div className="flex items-center gap-4 mt-2">
          <ActionBtn icon={Heart} label={comment.like_count || ""} onClick={canAct ? like : undefined} active={comment.liked_by_me} activeColor="#E91E8C" testId="comment-like-btn" />
          {!isReply && canAct && <ActionBtn icon={MessageCircle} label="Responder" onClick={requireAuth(() => setReplying(true))} testId="comment-reply-btn" />}
          {comment.is_mine && canAct && !editing && <ActionBtn icon={Pencil} label="Editar" onClick={() => setEditing(true)} testId="comment-edit-btn" />}
          {canDelete && <ActionBtn icon={Trash2} label="Borrar" onClick={remove} testId="comment-delete-btn" />}
          {!comment.is_mine && canAct && <ActionBtn icon={Flag} label={comment.reported_by_me ? "Reportado" : ""} onClick={report} active={comment.reported_by_me} activeColor="#f87171" testId="comment-report-btn" />}
        </div>
        {replying && (
          <div className="mt-3">
            <CommentComposer onSubmit={sendReply} onCancel={() => setReplying(false)} placeholder={`Responder a ${comment.author.name}...`} submitLabel="Responder" accent={accent} autoFocus testId="reply-composer" />
          </div>
        )}
      </div>
    </div>
  );
}
